import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, renderList as _renderList } from "vue"

const _hoisted_1 = {
  class: "mb-4",
  "data-cy": "accounting-mode-change-invoice-message"
}
const _hoisted_2 = { class: "text-body-2" }
const _hoisted_3 = {
  class: "mb-4",
  "data-cy": "accounting-mode-change-message"
}
const _hoisted_4 = { class: "text-body-2" }
const _hoisted_5 = { class: "mt-4" }
const _hoisted_6 = { class: "font-weight-medium mb-2" }
const _hoisted_7 = {
  class: "ml-4",
  "data-cy": "accounting-mode-change-matters"
}
const _hoisted_8 = {
  key: 0,
  class: "text-caption text-error d-flex align-start gap-1",
  "data-cy": "accounting-mode-change-reset-blocked"
}
const _hoisted_9 = {
  key: 0,
  class: "text-body-2 mt-4 mb-0",
  "data-cy": "accounting-mode-change-consequence"
}
const _hoisted_10 = { class: "text-caption text-grey-darken-1 mt-4 mb-0" }

import { computed, onBeforeUnmount, reactive } from 'vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlIcon from '@/components/LlIcon.vue';
import { useSettlementStore } from '@/modules/matters/settlement/stores/useSettlementStore';
import type { BlockingSettlement } from '@/models/settlementTypes';
import type { BlockingInvoice } from '@/models/invoiceTypes';

/** Past this many the list stops informing and starts filling the dialog. */
const MAX_LISTED_INVOICES = 5;

interface OpenParams {
  /** `true` when the mode the settlement was started under was Client Accounting. */
  oldModeIsClientAccounting: boolean;
  /** `true` for the mode the user just attempted to select on the QuickBooks tab. */
  newModeIsClientAccounting: boolean;
  /** The client the mode belongs to — the reset is client-scoped, not per settlement. */
  clientId: string;
  /** Every settlement under the client blocking the change (BR-16/BR-17). */
  settlements: BlockingSettlement[];
  /**
   * Unpaid or partly paid invoices under the client. Non-empty puts the
   * dialog in its refusal mode — no reset is offered, because none would help.
   */
  invoices?: BlockingInvoice[];
  /** `me.terms.Matter` — firms rename "Matter" and the toggle already honours it. */
  matterTerm?: string;
}

/**
 * Mounted imperatively by accountingModeGuard.ts via `vm.open(params)` into a host
 * it creates, because the callers are AngularJS controllers with no Vue parent to
 * hold a template ref. `open()` resolves `true` once Unlock & Reset succeeds,
 * `false` on dismiss.
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'AccountingModeChangeDialog',
  setup(__props, { expose: __expose }) {

const state = reactive({
  visible: false,
  oldModeIsClientAccounting: false,
  newModeIsClientAccounting: false,
  clientId: null as string | null,
  settlements: [] as BlockingSettlement[],
  invoices: [] as BlockingInvoice[],
  resetting: false,
  matterTerm: 'Matter',
});

const settlementStore = useSettlementStore();

let resolveOpen: ((didReset: boolean) => void) | null = null;
// Monotonic guard: a reset already in flight must not resolve a later open().
let openToken = 0;

const modeLabel = (isClientAccounting: boolean) =>
  isClientAccounting ? 'Client Accounting' : `${state.matterTerm} Accounting`;

const oldModeLabel = computed(() => modeLabel(state.oldModeIsClientAccounting));
const newModeLabel = computed(() => modeLabel(state.newModeIsClientAccounting));

const matterTermLower = computed(() => state.matterTerm.toLowerCase());

/**
 * The invoice refusal outranks the settlement branch: it admits no remedy inside the
 * dialog, so nothing below it should offer one. The guard only fills `invoices` when
 * the user is moving *into* Client Accounting, so this cannot fire the other way.
 */
const invoicesBlock = computed(() => state.invoices.length > 0);

/**
 * The mode being moved to, in the hyphenated form the refusal sentence reads in
 * ("…converting this account to client-accounting"). Lower-cased and hyphenated
 * rather than reusing `newModeLabel`, which is title-cased for use as a noun
 * elsewhere in this dialog and would read wrong mid-sentence.
 */
const newModeHyphenated = computed(() =>
  state.newModeIsClientAccounting
    ? 'client-accounting'
    : `${matterTermLower.value}-accounting`,
);

/**
 * The invoice numbers, capped — a client with forty open invoices would otherwise
 * push the action buttons off the bottom of the dialog and bury the instruction the
 * user actually has to follow. The overflow is counted rather than dropped silently,
 * because the remedy is "move them all back to Drafts" and a truncated list that
 * looked complete would send them back here.
 */
const invoiceReferenceList = computed(() => {
  const references = state.invoices.map(
    (invoice) => invoice.reference || 'no number',
  );
  const shown = references.slice(0, MAX_LISTED_INVOICES).join(', ');
  const remaining = references.length - MAX_LISTED_INVOICES;
  return remaining > 0 ? `${shown} and ${remaining} more` : shown;
});

/**
 * One blocked sibling refuses the whole change. The mode is client-wide, so
 * resetting only the resettable ones would leave the client half-migrated —
 * worse than refusing, because the surviving snapshots were derived under a mode
 * that no longer applies.
 */
const canResetAll = computed(
  () =>
    state.settlements.length > 0 &&
    state.settlements.every((settlement) => settlement.canReset),
);

/**
 * The single predicate behind both the Reset button and the title, so the two can
 * never disagree — the bug the title comment below records was exactly that.
 * `canResetAll` is already false in the invoice branch, since it arrives with no
 * settlements; the explicit term keeps that true if a caller ever sends both.
 */
const showResetOption = computed(() => canResetAll.value && !invoicesBlock.value);

const settlementNoun = computed(() =>
  state.settlements.length > 1
    ? `the settlements on ${state.settlements.length} ${matterTermLower.value}s`
    : `this ${matterTermLower.value}'s settlement`,
);

/**
 * Titled for what the user can do, not only for what they can't. The refusal
 * wording is correct when nothing can be reset; keeping it over a live Reset
 * button told the user "no" directly above the button that means "yes".
 */
const title = computed(() =>
  showResetOption.value
    ? `Reset ${state.settlements.length > 1 ? 'settlements' : 'settlement'} to change this setting`
    : "Can't Change Accounting Setting",
);

const listLabel = computed(() =>
  canResetAll.value
    ? `${state.settlements.length > 1 ? 'Settlements' : 'Settlement'} to reset`
    : `${state.settlements.length > 1 ? 'Settlements' : 'Settlement'} in progress`,
);

const blockingSummary = computed(() =>
  state.settlements.length > 1
    ? `these ${matterTermLower.value}s have settlements in progress`
    : `this ${matterTermLower.value} has a settlement in progress`,
);

const resetButtonLabel = computed(() =>
  state.settlements.length > 1 ? 'Reset Settlements' : 'Reset Settlement',
);

function open(params: OpenParams): Promise<boolean> {
  // A prior open() is still pending — settle it as superseded so its caller
  // never hangs on a promise that would otherwise be silently overwritten.
  resolveOpen?.(false);
  openToken += 1;

  state.oldModeIsClientAccounting = params.oldModeIsClientAccounting;
  state.newModeIsClientAccounting = params.newModeIsClientAccounting;
  state.clientId = params.clientId;
  state.settlements = params.settlements ?? [];
  state.invoices = params.invoices ?? [];
  state.matterTerm = params.matterTerm || 'Matter';
  state.resetting = false;
  state.visible = true;
  return new Promise((resolve) => {
    resolveOpen = resolve;
  });
}

function handleDismiss(): void {
  state.visible = false;
  resolveOpen?.(false);
  resolveOpen = null;
}

async function handleReset(): Promise<void> {
  if (!state.clientId || !canResetAll.value) return;
  // Capture this open()'s resolver and token: if it is superseded mid-request,
  // the success must not resolve whichever caller happens to be current.
  const resolveThis = resolveOpen;
  const token = openToken;
  state.resetting = true;
  try {
    await settlementStore.resetSettlementsForClient(state.clientId);
    resolveThis?.(true);
    if (token !== openToken) return;
    state.visible = false;
    resolveOpen = null;
  } catch {
    // Error already surfaced by the store (useSnackbarHandler.handleError).
  } finally {
    if (token === openToken) state.resetting = false;
  }
}

/**
 * The host app can be unmounted with a dialog still open — accountingModeGuard.ts
 * unmounts this island when Angular destroys the controller that created it, which
 * happens on navigation. Nothing else settles `open()`, so without this the caller's
 * `await openDialog(...)` never returns: its frame, holding the settlement
 * response, is retained for the rest of the session and its `.catch` never runs.
 *
 * Resolve `false` rather than rejecting — "the user did not reset" is the truth
 * here, and rejecting would surface a toast on whatever page they navigated to.
 * Bumping the token also marks any in-flight reset as superseded, so it does not
 * write state back into a component that is gone.
 */
onBeforeUnmount(() => {
  openToken += 1;
  resolveOpen?.(false);
  resolveOpen = null;
});

__expose({ open });

return (_ctx: any,_cache: any) => {
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!

  return (state.visible)
    ? (_openBlock(), _createBlock(_component_v_dialog, {
        key: 0,
        "model-value": state.visible,
        "max-width": "520",
        persistent: "",
        "aria-label": title.value,
        "data-cy": "accounting-mode-change-dialog"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, { "data-cy": "accounting-mode-change-title" }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(title.value), 1)
                ]),
                _: 1
              }),
              (invoicesBlock.value)
                ? (_openBlock(), _createBlock(_component_v_card_text, { key: 0 }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_1, [
                        _createVNode(LlIcon, {
                          icon: "mdi-alert",
                          color: "warning",
                          class: "mr-2"
                        }),
                        _createElementVNode("span", _hoisted_2, " One or more " + _toDisplayString(matterTermLower.value) + "s belonging to this client has an unpaid or partially paid invoice in the Manage Invoices page. Please move invoice" + _toDisplayString(state.invoices.length > 1 ? 's' : '') + " (" + _toDisplayString(invoiceReferenceList.value) + ") back to Drafts before converting this account to " + _toDisplayString(newModeHyphenated.value) + ". ", 1)
                      ])
                    ]),
                    _: 1
                  }))
                : (_openBlock(), _createBlock(_component_v_card_text, { key: 1 }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_3, [
                        _createVNode(LlIcon, {
                          icon: "mdi-alert",
                          color: "warning",
                          class: "mr-2"
                        }),
                        _createElementVNode("span", _hoisted_4, [
                          (canResetAll.value)
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                _cache[0] || (_cache[0] = _createTextVNode(" Switching from ", -1)),
                                _createElementVNode("strong", null, _toDisplayString(oldModeLabel.value), 1),
                                _cache[1] || (_cache[1] = _createTextVNode(" to ", -1)),
                                _createElementVNode("strong", null, _toDisplayString(newModeLabel.value), 1),
                                _createTextVNode(" resets " + _toDisplayString(settlementNoun.value) + ". ", 1)
                              ], 64))
                            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                _cache[2] || (_cache[2] = _createTextVNode(" This client's QuickBooks accounting setting can't be changed from ", -1)),
                                _createElementVNode("strong", null, _toDisplayString(oldModeLabel.value), 1),
                                _cache[3] || (_cache[3] = _createTextVNode(" to ", -1)),
                                _createElementVNode("strong", null, _toDisplayString(newModeLabel.value), 1),
                                _createTextVNode(" right now — " + _toDisplayString(blockingSummary.value) + ". ", 1)
                              ], 64)),
                          (state.settlements.length > 1)
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                _createTextVNode(" The setting applies to every " + _toDisplayString(matterTermLower.value) + " under this client, so they have to be reset together. ", 1)
                              ], 64))
                            : _createCommentVNode("", true)
                        ])
                      ]),
                      _createElementVNode("div", _hoisted_5, [
                        _createElementVNode("div", _hoisted_6, _toDisplayString(listLabel.value), 1),
                        _createElementVNode("ul", _hoisted_7, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(state.settlements, (settlement) => {
                            return (_openBlock(), _createElementBlock("li", {
                              key: settlement.settlementId,
                              class: "mb-1"
                            }, [
                              _createTextVNode(_toDisplayString(settlement.matterName) + " ", 1),
                              (!settlement.canReset && settlement.resetBlockedReason)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                                    _createVNode(LlIcon, {
                                      icon: "mdi-lock",
                                      color: "error",
                                      size: "13",
                                      class: "flex-shrink-0 mt-1"
                                    }),
                                    _createTextVNode(" " + _toDisplayString(settlement.resetBlockedReason), 1)
                                  ]))
                                : _createCommentVNode("", true)
                            ]))
                          }), 128))
                        ])
                      ]),
                      (canResetAll.value)
                        ? (_openBlock(), _createElementBlock("p", _hoisted_9, " Resetting returns " + _toDisplayString(state.settlements.length > 1 ? 'each settlement' : 'the settlement') + " to Step 1. Confirmed deposits, the fee calculation and any generated statement are cleared. ", 1))
                        : _createCommentVNode("", true),
                      _createElementVNode("p", _hoisted_10, [
                        _createTextVNode(" If invoices were already created in QuickBooks at the " + _toDisplayString(oldModeLabel.value) + " level — especially under the \"Client pays expenses as they go\" arrangement — they may need to be manually recreated at the " + _toDisplayString(newModeLabel.value) + " level. Email ", 1),
                        _cache[4] || (_cache[4] = _createElementVNode("a", { href: "mailto:support@myleanlaw.com" }, "support@myleanlaw.com", -1)),
                        _cache[5] || (_cache[5] = _createTextVNode(" if you'd like help. ", -1))
                      ])
                    ]),
                    _: 1
                  })),
              _createVNode(_component_v_card_actions, { class: "pa-4" }, {
                default: _withCtx(() => [
                  (showResetOption.value)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _createVNode(LlSecondaryButton, {
                          disabled: state.resetting,
                          "data-cy": "accounting-mode-change-acknowledge",
                          onClick: handleDismiss
                        }, {
                          default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                            _createTextVNode(" Cancel ", -1)
                          ]))]),
                          _: 1
                        }, 8, ["disabled"]),
                        _createVNode(_component_v_spacer),
                        _createVNode(LlSecondaryButton, {
                          color: "error",
                          variant: "outlined",
                          loading: state.resetting,
                          disabled: state.resetting,
                          "data-cy": "accounting-mode-change-reset",
                          onClick: handleReset
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(resetButtonLabel.value), 1)
                          ]),
                          _: 1
                        }, 8, ["loading", "disabled"])
                      ], 64))
                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        _createVNode(_component_v_spacer),
                        _createVNode(LlSecondaryButton, {
                          variant: "outlined",
                          "data-cy": "accounting-mode-change-acknowledge",
                          onClick: handleDismiss
                        }, {
                          default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                            _createTextVNode(" OK ", -1)
                          ]))]),
                          _: 1
                        })
                      ], 64))
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["model-value", "aria-label"]))
    : _createCommentVNode("", true)
}
}

})